import { Params } from '@angular/router';
import { Icon } from './icon.type';
import { Signal } from '@angular/core';
import { __ } from '../functions/object.functions';

// ─────────────────────────────────────────────
// NAVIGATION TYPES
// ─────────────────────────────────────────────

interface NavExternalLink {
    link: string;
    target?: '_blank' | '_self' | '_parent' | '_top' | string;
    rel?: string;

    route?: never;
    isActive?: never;
    queryParams?: never;
    routerLinkActiveOptions?: never;
}

interface NavInternalRoute {
    route: string | any[];
    isActive: Signal<boolean>;
    queryParams?: Params;
    routerLinkActiveOptions?: { exact: boolean };

    link?: never;
    target?: never;
    rel?: never;
}

interface NavNoDestination {
    link?: never;
    route?: never;
    isActive?: never;
    queryParams?: never;
    routerLinkActiveOptions?: never;
    target?: never;
    rel?: never;
}

export type NavDestination = NavExternalLink | NavInternalRoute | NavNoDestination;

// ─────────────────────────────────────────────
// NAVIGATION LABEL
// ─────────────────────────────────────────────

type NavLabel = {
    label: string;
    isTranslatable?: boolean;
}

// ─────────────────────────────────────────────
// NAVIGATION ITEM
// ─────────────────────────────────────────────

export type NavItem = NavDestination & NavLabel & {
    id: string;
    icon?: Icon;
    hidden?: boolean;
    disabled?: boolean;
    roles?: string[];
    children?: NavItem[];
    expandedByDefault?: boolean;
    /**
     * A live counter chip on the item (e.g. due Wiedervorlagen) — a SIGNAL so the sidebar
     * updates without rebuilding the navigation. Falsy values (0, null) hide the chip.
     */
    badge?: () => number | null;
}

// ─────────────────────────────────────────────
// HELPER TYPES
// ─────────────────────────────────────────────

export type NavGroup = NavItem & { children: [NavItem, ...NavItem[]] };

export type NavRouteItem = NavItem & NavInternalRoute;

export type NavLinkItem = NavItem & NavExternalLink;

export type NavNoDestinationItem = NavItem & NavNoDestination;

export type AppNavigation = NavItem[] | NavGroup[];

// ─────────────────────────────────────────────
// TYPE GUARDS
// ─────────────────────────────────────────────

/**
 * Type guard to check if a navigation item is a navigation group.
 * 
 * @param {NavItem} item - The navigation item to check.
 * @returns True if the item is a navigation group, false otherwise.
 */
export function isNavGroup(item: NavItem): item is NavGroup {
    return Array.isArray(item.children) && item.children.length > 0;
}

/**
 * Type guard to check if a navigation item is a navigation route item.
 * 
 * @param {NavItem} item - The navigation item to check.
 * @returns True if the item is a navigation route item, false otherwise.
 */
export function isNavRouteItem(item: NavItem): item is NavRouteItem {
    return !__.IsNullOrUndefinedOrEmpty(item.route);
}

/**
 * Type guard to check if a navigation item is a navigation link item.
 * 
 * @param {NavItem} item - The navigation item to check.
 * @returns True if the item is a navigation link item, false otherwise.
 */
export function isNavLinkItem(item: NavItem): item is NavLinkItem {
    return __.IsNullOrUndefinedOrEmpty(item.route) && !__.IsNullOrUndefinedOrEmpty(item.link);
}

/**
 * Type guard to check if a navigation item is a navigation no destination item.
 * 
 * @param {NavItem} item - The navigation item to check.
 * @returns True if the item is a navigation no destination item, false otherwise.
 */
export function isNavNoDestinationItem(item: NavItem): item is NavNoDestinationItem {
    return __.IsNullOrUndefinedOrEmpty(item.route) && __.IsNullOrUndefinedOrEmpty(item.link);
}